/**
 * useWebSocket Hook
 *
 * Provides WebSocket connectivity in React components with:
 * - Automatic connection management tied to component lifecycle
 * - Connection state as React state
 * - Message subscription with automatic cleanup
 * - Terreiro-scoped connections
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  WSConnectionState,
  WSMessageType,
  wsService,
  type WSConfig,
  type WSEventListener,
  type WSMessage,
} from "../api/websocketService";

/**
 * Hook options
 */
interface UseWebSocketOptions {
  /** Terreiro ID to scope the connection */
  terreiroId?: string;
  /** Medium ID to scope personal-profile connection */
  mediumId?: string;
  /** Authenticated user-level social messaging scope */
  scope?: "social";
  /** Auto-connect when the hook mounts (default: true) */
  autoConnect?: boolean;
  /** Whether the connection is enabled (default: true) */
  enabled?: boolean;
  /** Enable debug logging (default: false) */
  debug?: boolean;
  /** Additional WebSocket configuration */
  config?: Partial<WSConfig>;
}

/**
 * Hook return type
 */
interface UseWebSocketReturn {
  /** Current connection state */
  state: WSConnectionState;
  /** Whether connected */
  isConnected: boolean;
  /** Connect to WebSocket */
  connect: () => void;
  /** Disconnect from WebSocket */
  disconnect: () => void;
  /** Send a message */
  send: <T>(message: WSMessage<T>) => boolean;
  /** Subscribe to a message type (returns unsubscribe function) */
  subscribe: <T>(
    type: WSMessageType,
    listener: WSEventListener<T>
  ) => () => void;
  /** Connection statistics */
  stats: ReturnType<typeof wsService.getStats>;
}

/**
 * useWebSocket Hook
 *
 * Manages WebSocket connection state and provides subscription utilities.
 *
 * @example
 * ```tsx
 * const { isConnected, subscribe, send } = useWebSocket({
 *   terreiroId: currentTerreiro?.id,
 *   autoConnect: true,
 * });
 *
 * useEffect(() => {
 *   const unsubscribe = subscribe(WSMessageType.REQUEST_CREATED, (message) => {
 *     console.log('New request:', message.payload);
 *   });
 *   return unsubscribe;
 * }, [subscribe]);
 * ```
 */
export function useWebSocket(
  options: UseWebSocketOptions = {}
): UseWebSocketReturn {
  const {
    terreiroId,
    mediumId,
    scope,
    autoConnect = true,
    enabled = true,
    config = {},
  } = options;

  const [state, setState] = useState<WSConnectionState>(wsService.state);
  const [stats, setStats] = useState(() => wsService.getStats());
  const terreiroIdRef = useRef(terreiroId);
  const mediumIdRef = useRef(mediumId);
  const scopeRef = useRef(scope);
  const configRef = useRef(config);
  const mountedRef = useRef(true);
  const prevScopeRef = useRef<string | null>(
    scope
      ? `scope:${scope}`
      : terreiroId
      ? `terreiro:${terreiroId}`
      : mediumId
      ? `medium:${mediumId}`
      : null
  );

  // Keep refs up-to-date without triggering effects
  terreiroIdRef.current = terreiroId;
  mediumIdRef.current = mediumId;
  scopeRef.current = scope;
  configRef.current = config;

  // Subscribe to state changes (event-driven, replaces 500ms polling)
  useEffect(() => {
    mountedRef.current = true;

    // Set initial state
    setState(wsService.state);
    setStats(wsService.getStats());

    const unsubscribe = wsService.onStateChange(() => {
      if (mountedRef.current) {
        setState(wsService.state);
        setStats(wsService.getStats());
      }
    });

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, []);

  // Single connection management effect.
  // Depends only on stable primitives — NOT on the `config` object (which
  // creates a new reference every render and caused the effect to re-fire
  // on every render, generating duplicate connect() calls).
  useEffect(() => {
    const nextScope = scope
      ? `scope:${scope}`
      : terreiroId
      ? `terreiro:${terreiroId}`
      : mediumId
      ? `medium:${mediumId}`
      : null;

    if (!autoConnect || !enabled || !nextScope) {
      if (wsService.state !== ("DISCONNECTED" as WSConnectionState)) {
        wsService.disconnect();
      }
      prevScopeRef.current = nextScope;
      return;
    }

    const scopeChanged = prevScopeRef.current !== nextScope;
    prevScopeRef.current = nextScope;

    // If profile scope changed, disconnect previous connection first
    if (
      scopeChanged &&
      wsService.state !== ("DISCONNECTED" as WSConnectionState)
    ) {
      wsService.disconnect();
    }

    wsService.connect({
      ...configRef.current,
      terreiroId,
      mediumId,
      scope,
    });

    // Don't disconnect on unmount — the singleton service persists.
    // Disconnection should be explicit (e.g., on logout or profile switch).
  }, [autoConnect, enabled, terreiroId, mediumId, scope]);

  const connect = useCallback(() => {
    wsService.connect({
      ...configRef.current,
      terreiroId: terreiroIdRef.current,
      mediumId: mediumIdRef.current,
      scope: scopeRef.current,
    });
  }, []);

  const disconnect = useCallback(() => {
    wsService.disconnect();
  }, []);

  const send = useCallback(<T,>(message: WSMessage<T>) => {
    return wsService.send(message);
  }, []);

  const subscribe = useCallback(
    <T,>(type: WSMessageType, listener: WSEventListener<T>) => {
      return wsService.on(type, listener);
    },
    []
  );

  return {
    state,
    isConnected: state === ("CONNECTED" as WSConnectionState),
    connect,
    disconnect,
    send,
    subscribe,
    stats,
  };
}

/**
 * useWebSocketMessage Hook
 *
 * Convenience hook to subscribe to a single message type.
 *
 * @example
 * ```tsx
 * useWebSocketMessage(WSMessageType.REQUEST_CREATED, (message) => {
 *   console.log('New request:', message.payload);
 *   queryClient.invalidateQueries({ queryKey: ['requests'] });
 * });
 * ```
 */
export function useWebSocketMessage<T = unknown>(
  type: WSMessageType,
  handler: WSEventListener<T>,
  deps: React.DependencyList = []
): void {
  const handlerRef = useRef(handler);

  // Update handler ref
  useEffect(() => {
    handlerRef.current = handler;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handler, ...deps]);

  useEffect(() => {
    const unsubscribe = wsService.on<T>(type, (message) => {
      handlerRef.current(message);
    });

    return unsubscribe;
  }, [type]);
}

/**
 * useWebSocketConnection Hook
 *
 * Simple hook to just get connection state without managing connection.
 * Useful for displaying connection status in UI.
 *
 * @example
 * ```tsx
 * const { isConnected, state } = useWebSocketConnection();
 *
 * return (
 *   <div>
 *     Connection: {isConnected ? '🟢 Online' : '🔴 Offline'}
 *   </div>
 * );
 * ```
 */
export function useWebSocketConnection() {
  const [state, setState] = useState<WSConnectionState>(wsService.state);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  useEffect(() => {
    const unsubscribe = wsService.onStateChange(() => {
      setState(wsService.state);
      const stats = wsService.getStats();
      setReconnectAttempts(stats.reconnectAttempts ?? 0);
    });

    return unsubscribe;
  }, []);

  return {
    state,
    isConnected: state === ("CONNECTED" as WSConnectionState),
    isConnecting:
      state === ("CONNECTING" as WSConnectionState) ||
      state === ("RECONNECTING" as WSConnectionState),
    isError: state === ("ERROR" as WSConnectionState),
    reconnectAttempts,
  };
}
